/* ============================================================
   UI-примитивы нового дизайна «Федерация киберспорта СКО».
   ============================================================ */
import { useEffect, useRef, useState } from 'react';
import home2026Background from './assets/home-2026/stage-bg.webp';
import home2026MobileBackground from './assets/home-2026/mobile-bg.webp';

/** Reveal-on-scroll (аналог .rv из макета) */
export function Rv({ as: Tag = 'div', delay = 0, className = '', style, children, ...rest }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { setInView(true); io.disconnect(); }
        });
      },
      // срабатываем заранее (за 12% до входа в вьюпорт) — никакого «подлага» при скролле
      { rootMargin: '0px 0px 12% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={`fed-rv ${inView ? 'in' : ''} ${className}`}
      style={{ animationDelay: delay ? `${delay}s` : undefined, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** Анимированный счётчик (аналог .count из макета) */
export function CountUp({ target = 0, className = '', style }) {
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const run = () => {
      if (started.current) return;
      started.current = true;
      const dur = 1400;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * eased).toLocaleString('ru-RU');
        if (p < 1) requestAnimationFrame(step);
        else el.textContent = target.toLocaleString('ru-RU');
      };
      requestAnimationFrame(step);
    };
    if (typeof IntersectionObserver === 'undefined') { run(); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { run(); io.disconnect(); } });
    });
    io.observe(el);
    return () => io.disconnect();
  }, [target]);
  return <span ref={ref} className={className} style={style}>0</span>;
}

/** Моно-надзаголовок секции */
export function Eyebrow({ children, color, className = '', style }) {
  return (
    <div className={`fed-eyebrow ${className}`} style={{ color, ...style }}>
      {children}
    </div>
  );
}

/** Заголовок страницы с надзаголовком и подводкой */
export function PageHead({ eyebrow, eyebrowColor, title, sub, center = false, mb = 26 }) {
  return (
    <Rv className={center ? 'text-center' : ''} style={{ marginBottom: mb }}>
      {eyebrow && <Eyebrow color={eyebrowColor} style={{ marginBottom: 12 }}>{eyebrow}</Eyebrow>}
      <h1 style={{ fontSize: 'clamp(38px,6vw,64px)' }}>{title}</h1>
      {sub && (
        <p className={`text-[16px] leading-[1.6] text-fg-soft mt-3.5 max-w-[560px] ${center ? 'mx-auto' : ''}`}>{sub}</p>
      )}
    </Rv>
  );
}

/** Статус-пилюля (mono, uppercase) */
export function Pill({ children, color = 'var(--fed-fg-muted)', bg = 'var(--fed-surface-2)', border = 'var(--fed-line)', dot = false, className = '', style }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-mono uppercase ${className}`}
      style={{ padding: '5px 11px', fontSize: 10, letterSpacing: '.1em', color, background: bg, border: `1px solid ${border}`, ...style }}
    >
      {dot && <span className="fed-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }} />}
      {children}
    </span>
  );
}

/** Поле формы с mono-меткой */
export function Field({ label, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-[7px] ${className}`}>
      <span className="fed-label">{label}</span>
      {children}
    </label>
  );
}

/** Сегментный переключатель-таббар (пилюля с красным активом) */
export function Seg({ items, className = '', height = 44, minWidth }) {
  return (
    <div className={`flex gap-1.5 p-[5px] bg-surface border border-line rounded-[13px] overflow-x-auto ${className}`}>
      {items.map((t) => (
        <button
          key={t.key}
          onClick={t.onClick}
          className="flex-1 border-none rounded-[9px] cursor-pointer font-display font-semibold uppercase tracking-[.03em] transition-colors duration-200"
          style={{
            minWidth, height, fontSize: 14.5,
            background: t.active ? 'linear-gradient(120deg,var(--fed-accent-bright),var(--fed-accent-deep))' : 'transparent',
            color: t.active ? '#fff' : 'var(--fed-fg-muted)',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Логотип федерации (круг с красной обводкой и свечением) */
export function FedLogo({ size = 44, glow = true, className = '', style }) {
  return (
    <img
      src="/assets/logo-federation.png"
      alt="СКО"
      className={className}
      style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
        border: '1.5px solid rgba(225,18,23,.55)',
        boxShadow: glow ? '0 0 18px -4px var(--fed-accent-glow)' : undefined,
        ...style,
      }}
    />
  );
}

/** Фиксированный фон витрины. На публичных страницах добавляется адаптивный арт-слой. */
export function FedBackground({ artwork = false, home2026 = false }) {
  return (
    <div
      aria-hidden="true"
      className={`fed-site-background fixed inset-0 z-0 overflow-hidden pointer-events-none${artwork ? ' has-artwork' : ''}${home2026 ? ' home-2026-artwork' : ''}`}
      style={{ background: 'var(--fed-bg)' }}
    >
      {artwork && (
        <div className="fed-home-background-art absolute inset-0">
          {home2026 ? (
            <>
              <img className="fed-background-image fed-background-image-home-2026 fed-background-image-home-desktop" src={home2026Background} alt="" />
              <img className="fed-background-image fed-background-image-home-2026 fed-background-image-home-mobile" src={home2026MobileBackground} alt="" />
            </>
          ) : (
            <>
              <img className="fed-background-image fed-background-image-desktop" src="/assets/новый фон 2.png?v=3" alt="" />
              <img className="fed-background-image fed-background-image-mobile" src="/assets/новый фон 1.png?v=3" alt="" />
            </>
          )}
          <div className="fed-home-background-shade absolute inset-0" />
        </div>
      )}
      <div
        className="fed-background-glow absolute"
        style={{
          top: '-34%', left: '50%', width: '120vw', height: '56vh', transform: 'translateX(-50%)',
          background: 'radial-gradient(ellipse at center,rgba(225,18,23,.13),transparent 62%)',
          filter: 'blur(38px)',
        }}
      />
    </div>
  );
}
